import type { MonitoringExtraDimension } from '@protector/entities/monitoring/models';
import type { CurrentSeasonArea } from '@protector/entities/property/models';
import { getCustomFormTimelineRecords } from '@protector/entities/timeline/custom-forms.services';
import { EventType } from '@protector/entities/timeline/constants';
import type { DateOnlyString, Dictionary, Moment, UUID } from '@protector/utils/types';
import axios from 'axios';
import axiosObservable from 'axios-observable';
import type { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { CORE_SERVICES_API_URL, PROTECTOR_API_URL, PROTECTOR_CROPWISE_API_URL } from 'config/constants';
import { coreServicesApiClient, protectorCropwiseApiClient } from 'core/services/apiGateway/index';
import { getCurrentLanguage } from 'core/utils/functions';
import type { Position } from 'geojson';
import type { PhenologicalStagesModel } from 'pages/disease-risk/disease-risk.models';
import type { Observable } from 'rxjs';
import { defer, forkJoin, of } from 'rxjs';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { catchError, map } from 'rxjs/operators';
import type { IEditAreaRequestParams } from './area-day-info/components/area-day-info.resume.models';
import type { MonitoringDetailPhenomenon } from './monitoring.models';
import { getDaysWithoutMonitoringResponseSchema, getTimesMonitoredResponseSchema } from './schemas/days-without-monitoring-schema';
import { getDaysWithoutSprayingResponseSchema } from './schemas/days-without-spraying-schema';
import { getCategoryWithPhenomenaResponseSchema } from './schemas/phenomena-with-category-schema';
import { productsByCategorySchema, type ProductsByCategory } from './schemas/products-with-category-schema';
import { mergeCustomFormTimelineEvents, sanitizeTimelineWindowEvents } from './timeline.domain';
import { trapWindowResponseSchema, type TrapWindowResponse } from './area-day-info/components/area-day-info-trap-info/trap-info.models';
import { rainGaugeWindowResponseSchema } from './area-day-info/components/area-day-info-rain-gauge-info/rain-gauge-info.models';
import {
  type GetCategoryWithPhenomenaResponse,
  type GetDaysWithoutMonitoringResponse,
  type GetDaysWithoutSprayingResponse,
  type GetEventsParams,
  type GetTimelineWindowsEventsProps,
  type GetTimesMonitoredResponse,
  type RequestTracking,
  type RequestTrackingExport,
  type SaveEditAreaInfoResponse,
  type TimelineBarPage,
  type TimelineWindow,
  type TimelineWindowEvents,
  type TimelineWindowEventsFromBff,
  type TrackingAssigneeResponse
} from './timeline.models';
import { getIgnoreListEvents, transformWindowEventsResponse } from './timeline.utils';

const protectorApiUrl = PROTECTOR_API_URL ?? 'http://localhost:8080';
const coreServicesApiUrl = CORE_SERVICES_API_URL ?? 'http://localhost:8080';
const cropwiseApiUrl = PROTECTOR_CROPWISE_API_URL ?? 'http://localhost:8080';

const trackings = `${protectorApiUrl}/v1/tracking/query`;
const getTimelineWindowEventsUrl = (options: { showFlaming?: boolean; showEmergence?: boolean } = {}): string => {
  let base = `${protectorApiUrl}/v1/timeline/window?include=IRRIGATION,HARVESTING,PLANTING,DETASSELING,FLOWERING,ROGUING,MALE_DESTRUCTION`;
  if (options.showEmergence) {
    base = `${base},EMERGENCE`;
  }
  if (options.showFlaming) {
    base = `${base},FLAMING`;
  }
  return base;
};

const eventPages = `${protectorApiUrl}/v1/timeline/events`;
const timelinePropertiesEvents = `${protectorApiUrl}/v1/timeline/properties`;
const monitoring = `${protectorApiUrl}/v1/timeline`;
const phenology = `${protectorApiUrl}/v1/phenology`;

const normalizeAcceptLanguage = (language: string): string => {
  const [languageCode, regionCode] = language.split('-');

  return regionCode ? `${languageCode.toLowerCase()}-${regionCode.toUpperCase()}` : languageCode.toLowerCase();
};

/** @deprecated v1/season-areas will be deprecated in the future */
const editAreaUrl = `${coreServicesApiUrl}/v1/season-areas`;
const samplingphotosURL = `${protectorApiUrl}/v1/timeline/photo/`;

/** @deprecated v1/season-areas will be deprecated in the future, if possible use POST v2/season-fields/list */
const seasonAreasUrl = `${coreServicesApiUrl}/v1/season-areas`;

export const getStationLatestPhoto = (propertyId: UUID, stationId: UUID): Observable<string> => {
  const stationLatestPhotoURL = `${protectorApiUrl}/api/v1/properties/${propertyId}/stations/${stationId}/latest-photo`;
  return axiosObservable.get<string>(stationLatestPhotoURL).pipe(
    // data is photo url
    map(({ data }) => (data === 'default backend - 404' ? '' : data)),
    catchError(() => of(''))
  );
};

export const getSamplingPhoto = (propertyId: UUID, photoId: UUID): Observable<string> => {
  const fullSamplingPhotoURL = `${samplingphotosURL}${propertyId}/${photoId}.png`;
  return axiosObservable.get<string>(fullSamplingPhotoURL).pipe(
    map(({ data }) => (data === 'default backend - 404' ? '' : data)),
    catchError(() => of(''))
  );
};

export const MAX_WINDOW_EVENTS = 8;

export const getEvents = ({
  companyId,
  propertyId,
  areaId,
  seasonIds,
  flags,
  ignoredEvents
}: GetEventsParams): Observable<TimelineWindow[]> => {
  const flagIgnoreEvents = getIgnoreListEvents(flags);
  const userIgnoredEvents = ignoredEvents ? [...ignoredEvents] : [];
  const ignored = [...new Set([...flagIgnoreEvents, ...userIgnoredEvents])];
  const ignore_events = ignored.filter(event => event !== EventType.CUSTOM_FORM);
  const shouldGetTrapsAndPluviometer = flags?.P40_53000_render_pluviometer_trap;
  const data = {
    property_id: propertyId,
    area_id: areaId,
    season_ids: seasonIds,
    ignore_events
  };

  const path = shouldGetTrapsAndPluviometer ? `${eventPages}?bypass_monitoring_filter=true` : eventPages;

  const forms =
    companyId && propertyId && areaId && !ignored.includes(EventType.CUSTOM_FORM)
      ? defer(() => getCustomFormTimelineRecords(companyId, propertyId)).pipe(
          catchError(error => {
            captureError(error, { domain: 'Timeline', errorType: 'CustomFormsEventsQuery' });
            return of([]);
          })
        )
      : of([]);

  return forkJoin({ response: axiosObservable.post<TimelineBarPage>(path, data), records: forms }).pipe(
    map(({ response, records }) => {
      const windows = sanitizeTimelineWindowEvents(response.data.window);
      if (!companyId || !propertyId || !areaId || records.length === 0) return windows;
      return mergeCustomFormTimelineEvents(windows, records, companyId, propertyId, areaId);
    })
  );
};

export const getWindowEvents = (
  property_id: string | null,
  area_id: string,
  season_ids: string[],
  start: Moment,
  end: Moment,
  options: { showFlaming?: boolean; showEmergence?: boolean } = {}
): AxiosObservable<TimelineWindowEvents> => {
  const url = getTimelineWindowEventsUrl(options);
  return axiosObservable.post<TimelineWindowEvents>(`${url}`, {
    property_id,
    area_id,
    season_ids,
    start,
    end
  });
};

export const saveEditAreaInfo = (requestParams: IEditAreaRequestParams) => {
  return axiosObservable.put<SaveEditAreaInfoResponse>(`${editAreaUrl}?update_harvesting_date=true`, requestParams);
};

export const getTimelineProperties = async (propertyId: UUID): Promise<PhenologicalStagesModel[]> => {
  return axios.get<PhenologicalStagesModel[]>(`${timelinePropertiesEvents}/${propertyId}/phenologies`).then(response => response.data);
};

export const getTrackings = (request: RequestTracking) => {
  return axiosObservable.post<TrackingAssigneeResponse>(trackings, request).pipe(
    map(response => response.data.assignees),
    catchError(error => {
      throw error;
    })
  );
};

export interface ExportTrackingResponse {
  assignees: { route: Position[]; name: string; timestamps: string[] }[];
}

export const getExportTracking = (request: RequestTrackingExport) => {
  return axiosObservable.post<ExportTrackingResponse>(trackings, request);
};

export const getSeasonAreas = (propertyId: UUID, pastSeasonAreas: boolean, seasonId?: string[]) => {
  return axiosObservable
    .get<CurrentSeasonArea[]>(
      `${seasonAreasUrl}?propertyId=${propertyId}&allowPastSeasonAreas=${pastSeasonAreas}${
        seasonId ? `&seasonIds=${seasonId.join(',')}` : ''
      }`
    )
    .pipe(map(response => response.data));
};

export interface MonitoringPointRequest {
  property_id: UUID;
  monitoring_id: UUID;
  date: DateOnlyString;
}

export interface MonitoringPointResponse {
  id: UUID;
  resources: MonitoringExtraDimension[];
  phenomena: MonitoringDetailPhenomenon[];
  notes: string[];
}

export const getMonitoringPoint = (props: MonitoringPointRequest): Observable<MonitoringPointResponse> => {
  const url = `${monitoring}/property/${props.property_id}/monitoring/${props.monitoring_id}?date=${props.date}`;
  return axiosObservable
    .get<MonitoringPointResponse>(url, { headers: { 'Content-Type': 'application/json' } })
    .pipe(map(response => response.data));
};

export const deletePhenology = (phenologyId: UUID) => axiosObservable.delete(`${phenology}/${phenologyId}/delete`);

export const getIncludeExtraTypes = (flags?: Dictionary<string | boolean> | null) => {
  const baseTypes = ['IRRIGATION', 'HARVESTING', 'PLANTING', 'FLOWERING', 'DETASSELING', 'ROGUING', 'MALE_DESTRUCTION'];
  const showFlaming = !!flags?.P40_56666_enable_flaming_operation_type;
  const showEmergence = !!flags?.P40_57972_enable_emergence_timeline;

  return [...baseTypes, ...(showEmergence ? ['EMERGENCE'] : []), ...(showFlaming ? ['FLAMING'] : [])];
};

export const getTimelineWindowsEvents = async ({
  property_id,
  area_id,
  season_ids,
  startEnd,
  company_id,
  result_ordering,
  include_extra_types,
  include_visualizations
}: GetTimelineWindowsEventsProps) => {
  const payload = {
    company_id,
    property_id,
    field_id: area_id,
    start_date: startEnd.start,
    end_date: startEnd.end,
    season_ids,
    include_extra_types,
    include_forms: true,
    result_ordering,
    ...(include_visualizations && { include_visualizations })
  };

  const pathAPI = `${cropwiseApiUrl}/web-server/v1/timeline/window`;

  const result = await axios.post<TimelineWindowEventsFromBff>(pathAPI, payload, {
    headers: {
      'x-property-id': property_id,
      'x-org-id': company_id
    }
  });

  return transformWindowEventsResponse<TimelineWindowEventsFromBff>({
    request: {
      property_id,
      area_id,
      season_ids,
      start: startEnd.start,
      end: startEnd.end
    },
    response: result?.data || [],
    enableMultiplePhotosNotes: true,
    isWindowEventsFromBFF: true
  });
};

export const getRainGaugeWindow = async ({
  orgId,
  propertyId,
  fieldId,
  start,
  end,
  language = getCurrentLanguage()
}: {
  orgId: string;
  propertyId: string;
  fieldId: string;
  start: string;
  end: string;
  language?: string;
}) => {
  const queryParams = new URLSearchParams({ org_id: orgId, property_id: propertyId, field_id: fieldId, start, end });
  const response = await protectorCropwiseApiClient.request({
    config: {
      url: `/web-server/v1/timeline/rain-gauge/window?${queryParams.toString()}`,
      method: 'GET',
      headers: { 'x-org-id': orgId, 'x-property-id': propertyId, 'Accept-Language': normalizeAcceptLanguage(language) }
    },
    outputSchema: rainGaugeWindowResponseSchema
  });
  return response.data;
};

export const getTrapWindow = async ({
  orgId,
  propertyId,
  fieldId,
  start,
  end,
  language = getCurrentLanguage()
}: {
  orgId: string;
  propertyId: string;
  fieldId: string;
  start: string;
  end: string;
  language?: string;
}): Promise<TrapWindowResponse> => {
  const queryParams = new URLSearchParams({
    org_id: orgId,
    property_id: propertyId,
    field_id: fieldId,
    start,
    end
  });
  const response = await protectorCropwiseApiClient.request({
    config: {
      url: `/web-server/v1/timeline/traps/window?${queryParams.toString()}`,
      method: 'GET',
      headers: {
        'x-org-id': orgId,
        'x-property-id': propertyId,
        'Accept-Language': normalizeAcceptLanguage(language)
      }
    },
    outputSchema: trapWindowResponseSchema
  });

  return response.data;
};

export const getCategoryWithPhenomena = async ({
  orgId,
  propertyId,
  seasonIds
}: {
  orgId: UUID;
  propertyId: UUID;
  seasonIds: UUID[];
}): Promise<GetCategoryWithPhenomenaResponse | undefined> => {
  const url = `/v1/properties/${propertyId}/categories/phenomena`;
  const response = await protectorCropwiseApiClient.request({
    config: {
      url,
      method: 'POST',
      headers: {
        'x-property-id': propertyId,
        'x-org-id': orgId
      },
      data: { season_ids: seasonIds }
    },
    outputSchema: getCategoryWithPhenomenaResponseSchema
  });
  return response?.data;
};

export const getDaysWithoutMonitoring = async ({
  propertyId,
  seasonIds,
  phenomenonIds
}: {
  orgId: UUID;
  propertyId: UUID;
  seasonIds: UUID[];
  phenomenonIds: UUID[];
}): Promise<GetDaysWithoutMonitoringResponse | undefined> => {
  const url = `/v1/observations/properties/${propertyId}/fields/days-without-observations`;
  const response = await coreServicesApiClient.request({
    config: {
      url,
      method: 'POST',
      data: { season_ids: seasonIds, phenomenon_ids: phenomenonIds }
    },
    outputSchema: getDaysWithoutMonitoringResponseSchema
  });
  return response?.data;
};

export const getTimesMonitored = async ({
  propertyId,
  seasonIds,
  phenomenonIds
}: {
  orgId: UUID;
  propertyId: UUID;
  seasonIds: UUID[];
  phenomenonIds: UUID[];
}): Promise<GetTimesMonitoredResponse | undefined> => {
  const url = `/v1/observations/properties/${propertyId}/fields/scouting-windows/count`;
  const response = await coreServicesApiClient.request({
    config: {
      url,
      method: 'POST',
      data: { season_ids: seasonIds, phenomenon_ids: phenomenonIds }
    },
    outputSchema: getTimesMonitoredResponseSchema
  });
  return response?.data;
};

export const getProductsByType = async ({
  orgId,
  propertyId,
  seasonIds
}: {
  orgId: UUID;
  propertyId: UUID;
  seasonIds?: UUID[];
}): Promise<ProductsByCategory | undefined> => {
  const params = new URLSearchParams();
  if (seasonIds?.length) {
    params.set('season_ids', seasonIds.join(','));
  }
  const queryString = params.toString();
  const urlSuffix = queryString ? `?${queryString}` : '';
  const url = `/web-server/v1/orgs/${orgId}/products-by-type${urlSuffix}`;
  const response = await protectorCropwiseApiClient.request({
    config: {
      url,
      method: 'GET',
      headers: {
        'x-org-id': orgId,
        'x-property-id': propertyId
      }
    },
    outputSchema: productsByCategorySchema
  });
  return response?.data;
};

export const getDaysWithoutSpraying = async ({
  orgId,
  propertyId,
  seasonIds,
  productIds
}: {
  orgId: UUID;
  propertyId: UUID;
  seasonIds: UUID[];
  productIds?: string[];
}): Promise<GetDaysWithoutSprayingResponse | undefined> => {
  const url = `/web-server/v1/timeline/days-without-spraying`;
  const response = await protectorCropwiseApiClient.request({
    config: {
      url,
      method: 'POST',
      headers: {
        'x-property-id': propertyId,
        'x-org-id': orgId
      },
      data: {
        org_id: orgId,
        property_id: propertyId,
        season_ids: seasonIds,
        ...(productIds?.length && { product_ids: productIds })
      }
    },
    outputSchema: getDaysWithoutSprayingResponseSchema
  });
  return response?.data;
};
